import { create } from 'zustand';
import { OrdemServico } from '../types';
import api from '../utils/api';

type FaseStatus = 'Pendente' | 'Em Rota' | 'Concluída';

interface OSStore {
  ordensServico: OrdemServico[];
  ordemAtiva: OrdemServico | null;
  isLoading: boolean;
  error: string | null;

  fetchOrdensAtribuidas: (options?: { silent?: boolean }) => Promise<void>;
  setOrdemAtiva: (ordem: OrdemServico | null) => void;
  updateFaseStatus: (
    faseId: number,
    status: FaseStatus,
    options?: { qr_code?: string }
  ) => Promise<void>;
  cancelarFase: (faseId: number) => Promise<void>;
  clearError: () => void;
}

export const useOSStore = create<OSStore>((set, get) => ({
  ordensServico: [],
  ordemAtiva: null,
  isLoading: false,
  error: null,

  fetchOrdensAtribuidas: async (options) => {
    if (!options?.silent) {
      set({ isLoading: true, error: null });
    } else {
      set({ error: null });
    }
    try {
      const response = await api.get<{ os: OrdemServico[] }>('/os');
      const ordens = response.data.os ?? [];

      const userStr = localStorage.getItem('motorista_user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.id;

      const ordensAtribuidas = userId
        ? ordens.filter((os: OrdemServico) =>
            os.fases?.some(
              (fase) =>
                fase.id_motorista_atribuido === userId &&
                fase.status_fase !== 'Concluída'
            )
          )
        : ordens;

      const currentAtiva = get().ordemAtiva;
      let novaOrdemAtiva = currentAtiva;
      if (currentAtiva) {
        novaOrdemAtiva =
          ordensAtribuidas.find((os) => os.id === currentAtiva.id) ??
          ordensAtribuidas[0] ??
          null;
      } else if (ordensAtribuidas.length > 0) {
        novaOrdemAtiva = ordensAtribuidas[0];
      }

      set({
        ordensServico: ordensAtribuidas,
        ordemAtiva: novaOrdemAtiva,
        isLoading: false,
      });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      set({
        error: e.response?.data?.error ?? 'Erro ao buscar ordens de serviço',
        isLoading: false,
      });
    }
  },

  setOrdemAtiva: (ordem) => {
    set({ ordemAtiva: ordem });
  },

  updateFaseStatus: async (faseId: number, status: FaseStatus, options) => {
    set({ error: null });
    try {
      const payload: { status: FaseStatus; qr_code?: string } = { status };
      if (options?.qr_code) {
        payload.qr_code = options.qr_code;
      }
      await api.put(`/os/${faseId}/status`, payload);
      const { ordensServico, ordemAtiva } = get();
      const ordensAtualizadas = ordensServico.map((os) => ({
        ...os,
        fases: os.fases.map((fase) => {
          if (fase.id !== faseId) return fase;
          if (status === 'Pendente') {
            return {
              ...fase,
              status_fase: status,
              timestamp_inicio_rota: undefined,
              timestamp_chegada: undefined,
            };
          }
          return { ...fase, status_fase: status };
        }),
      }));
      const novaOrdemAtiva =
        ordemAtiva &&
        ordensAtualizadas.find((os) => os.id === ordemAtiva.id);
      set({
        ordensServico: ordensAtualizadas,
        ordemAtiva: novaOrdemAtiva ?? null,
      });

      if (status === 'Concluída' || status === 'Pendente') {
        await get().fetchOrdensAtribuidas({ silent: true });
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string; code?: string } } };
      const message =
        e.response?.data?.error ?? 'Erro ao atualizar status da fase';
      set({ error: message });
      throw new Error(message);
    }
  },

  cancelarFase: async (faseId: number) => {
    await get().updateFaseStatus(faseId, 'Pendente');
  },

  clearError: () => {
    set({ error: null });
  },
}));
